import {
  type AlpacaNewsPost,
  type DbNewsPost,
  type TreeNewsPost,
  type TwitterPost,
} from '@/types/generated/public_api'

export type NewsSourceId = 'db_news' | 'alpaca' | 'twitter' | 'tree_news'

export const ALL_NEWS_SOURCES: NewsSourceId[] = ['db_news', 'alpaca', 'twitter', 'tree_news']

/**
 * Bump when the persisted `sourceFilter` config's meaning changes (e.g. a new
 * source is added and existing saved widgets need a one-time migration). Shared
 * by newsfeed.tsx, state/widget-instances.ts and widget-config-restore.ts —
 * lives here rather than on the widget component to avoid state/ importing a
 * component file. v2: adds 'tree_news', force-enabled once for configs saved
 * before this version existed.
 */
export const NEWS_SOURCE_FILTER_VERSION = 2

export type NewsSourcePost = DbNewsPost | AlpacaNewsPost | TwitterPost | TreeNewsPost

export interface NewsRow {
  rowId: string
  source: NewsSourceId
  timestampMs: number
  headline: string
  link?: string
  handle?: string
  tags?: string[]
  /** Only set for sources whose posts aren't all the same shape (Tree News). */
  variant?: 'social' | 'article'
  raw: NewsSourcePost
}

/**
 * Applies an arrival-ordered batch with the same semantics as calling
 * `setRows(prev => [row, ...prev without row])` once per item. The newest
 * arrival wins and appears first; duplicate ids never consume the row cap.
 */
export function mergeLiveNewsRows(
  previous: readonly NewsRow[],
  incoming: readonly NewsRow[],
  maxRows: number
): NewsRow[] {
  if (incoming.length === 0) return previous.slice()

  const seen = new Set<string>()
  const next: NewsRow[] = []

  for (let i = incoming.length - 1; i >= 0 && next.length < maxRows; i -= 1) {
    const row = incoming[i]
    if (seen.has(row.rowId)) continue
    seen.add(row.rowId)
    next.push(row)
  }

  for (const row of previous) {
    if (next.length >= maxRows) break
    if (seen.has(row.rowId)) continue
    seen.add(row.rowId)
    next.push(row)
  }

  return next
}

export function stripHtml(text: string) {
  return text.replace(/<[^>]*>/g, '')
}

function parseDbNewsTimestamp(ts: string) {
  const num = Number(ts)
  return isNaN(num) ? new Date(ts).getTime() : num
}

export function dbNewsPostToRow(post: DbNewsPost): NewsRow {
  const fallback = [
    post.newsType,
    post.ts,
    post.link,
    post.tweeterHandle,
    post.telegramId,
    stripHtml(post.text),
  ]
    .filter(Boolean)
    .join('|')

  return {
    rowId: `db_news:${post.newsType || 'unknown'}:${post.postId || fallback}`,
    source: 'db_news',
    timestampMs: parseDbNewsTimestamp(post.ts),
    headline: stripHtml(post.text),
    link: post.link || undefined,
    handle: post.tweeterHandle || undefined,
    tags: post.tags,
    raw: post,
  }
}

export function alpacaPostToRow(post: AlpacaNewsPost): NewsRow {
  return {
    rowId: `alpaca:${post.id}`,
    source: 'alpaca',
    timestampMs: new Date(post.createdAt).getTime(),
    headline: stripHtml(post.headline || post.summary),
    link: post.url || undefined,
    raw: post,
  }
}

export function twitterPostToRow(post: TwitterPost): NewsRow {
  return {
    rowId: `twitter:${post.postId}`,
    source: 'twitter',
    timestampMs: new Date(post.createdAt).getTime(),
    headline: post.text,
    link: `https://x.com/${post.handle}/status/${post.postId}`,
    handle: post.handle,
    raw: post,
  }
}

/**
 * Tree News posts share one message across two wire shapes (see TreeNewsPost's
 * own doc comment in the generated types): social posts omit `source` on the
 * wire (normalized here to `source === 'Twitter'`, text in `body`), everything
 * else is an article (headline in `title`). Branch on `source`, not on which
 * fields happen to be populated.
 */
export function treeNewsPostToRow(post: TreeNewsPost): NewsRow {
  const isSocial = post.source === 'Twitter'
  // Social `title` is "Display Name (@handle)", not a bare handle — pull just
  // the handle so LinkCell's own "@" prefix doesn't double up.
  const handle = isSocial ? (post.title.match(/\(@([^)]+)\)/)?.[1] ?? undefined) : undefined

  return {
    rowId: `tree_news:${post.id}`,
    source: 'tree_news',
    timestampMs: Number(post.time),
    headline: isSocial ? stripHtml(post.body) : post.title,
    link: post.url || undefined,
    handle,
    variant: isSocial ? 'social' : 'article',
    raw: post,
  }
}
